import { countries } from '@design-system/configs/countries'

/**
 * Global window interface extension for Riskified integration
 */
declare global {
  interface Window {
    RISKX: {
      go: (path: string) => void
    }
  }
}

/**
 * Composable for managing Riskified fraud detection and tracking.
 *
 * Handles session tracking, cookie management, and script loading for Riskified beacon.
 *
 * @async
 * @param {string} id - Unique identifier for the Riskified session
 * @returns {Promise<ScriptConfigOptions>} Riskified script configuration
 *
 * @example
 * // Typical usage in a Nuxt application
 * await useRiskified('user-unique-session-id')
 */
export const useRiskified = async (id: string) => {
  const configs = useConfigs()
  const config = useRuntimeConfig()

  /** Default shop URL for European market */
  const shopUrl = 'armani_eu.com'

  /** Reactive state for Riskified token */
  const _RFtoken = useState('rftoken', () => id)
  const RFtoken = unref(_RFtoken)

  /** Cookie duration set to 13 days in seconds */
  const cookieLifetime = 1123200

  /**
   * Manages Riskified session cookie with configurable max age
   * @type {Ref<{name: string | undefined}>}
   */
  const cookiesUserSession = useCookie<{
    name: string | undefined
  }>('riskified-cookie', { maxAge: cookieLifetime })

  /** Determine country configuration based on current country code */
  const country = countries.find(
    country => country.code === configs.value.country
  )

  // Special handling for specific session tokens
  if (
    !cookiesUserSession.value ||
    [
      // List of predefined session tokens
      '86745ac6283adbccf9859c684f7998ed',
      '0b7570da12132733e0da99c5b97aa127',
      'c83044295ae7ec4470df088da24425b8',
      '8dfd39bc85a76357c007455fb01d0dc7',
      'f364a1dcbc0b432df511e791dd504270',
      'b745b26f421aaf2b0461dc8566f15103',
      '9b487819b5538b9c0e70f38171682ced',
      'c6deeafb883a8ddf570d897c38562ad5',
      '6e870ee72a29a938469468a8eff12aaa',
      'f364a1dcbc0b432df511e791dd504270',
      'b745b26f421aaf2b0461dc8566f15103',
    ].includes(cookiesUserSession.value?.name ?? '')
  ) {
    cookiesUserSession.value = {
      name: RFtoken,
    }
  }

  /** Current session cookie */
  const cookie = cookiesUserSession.value.name

  /** Determine store domain, defaulting to European shop URL */
  const store_domain =
    country && country.RFshopUrl ? country.RFshopUrl : shopUrl

  /** Session identifier */
  const session_id = cookie

  /** Construct Riskified beacon URL with dynamic protocol, store domain, and session ID */
  const urlRisk: string = `${'https:' === document.location.protocol ? 'https://' : 'http://'}beacon.riskified.com?shop=${store_domain}&sid=${session_id}`

  /**
   * Load Riskified tracking script with deferred execution
   * @returns {Promise<ScriptConfigOptions>} Script configuration
   */
  return useScript({
    src: urlRisk,
    defer: true,
  })
}
